import { useCallback } from 'react';

import { INACCURACY } from 'constants/params';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useZoomCb } from 'components/app/_mediator/zoom/use-zoom-cb';
import { resetFinalcutUi, setFcPlayback } from './fc-controls.slice';
import { FcRangeTime, resetFinalcutData } from './fc-data.slice';
import { useFinalcutContext } from './finalcut.provider';

export const useFinalcutCb = () => {
  const { keepCursorInView } = useZoomCb();
  const { wsSeekTo, wsPlay, wsGetCursorTime } = useAudioTrackContext();
  const { fcPlayerPlay, fcPlayerSeekTo, fcPlayerReady } = useFinalcutContext();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const fcSeekTo = useCallback((value: Fraction) => {
    const { fcRangeTime } = selectData(store.getState());
    if (!fcRangeTime) return;
    fcPlayerReady.current = false;
    const { ws, player } = getSeekValues(value, fcRangeTime);
    fcPlayerSeekTo(player, 'fraction');
    wsSeekTo(ws, 'fraction');
  }, []);

  const fcSeekToEnd = useCallback(() => {
    const { fcRangeTime } = selectData(store.getState());
    if (!fcRangeTime) return;
    dispatch(setFcPlayback(false));
    fcSeekTo(fcRangeTime.endTime);
    keepCursorInView();
  }, []);

  const fcSeekToStart = useCallback(() => {
    const { fcRangeTime } = selectData(store.getState());
    if (!fcRangeTime) return;
    dispatch(setFcPlayback(false));
    fcSeekTo(fcRangeTime.startTime);
    keepCursorInView();
  }, []);

  const fcSelectClip = useCallback((cell: Index) => {
    const { duration, fcMarkers, fcStoryConfig } = selectData(store.getState());
    if (!duration || !fcStoryConfig) return;
    const { finishMarkerId } = fcStoryConfig;
    if (finishMarkerId && finishMarkerId < cell) fcSeekToEnd();
    else fcSeekTo(cell === 0 ? 0 : fcMarkers[cell - 1] / duration);
  }, []);

  const fcPlay = useCallback(() => {
    const { dragger, fcRangeTime } = selectData(store.getState());
    if (dragger || !fcPlayerReady.current || !fcRangeTime) return;
    fcPlayerPlay(true);
    wsPlay(true);
  }, []);

  const fcPause = useCallback(() => {
    fcPlayerPlay(false);
    wsPlay(false);
  }, []);

  const fcReset = useCallback((ui?: 'ui') => {
    if (!ui) dispatch(resetFinalcutData());
    dispatch(resetFinalcutUi());
  }, []);

  const fcIsEnded = useCallback(() => {
    const { fcRangeTime } = selectData(store.getState());
    if (!fcRangeTime) return;
    return wsGetCursorTime('fraction') >= fcRangeTime.endTime - INACCURACY;
  }, []);

  return { fcSeekTo, fcSeekToEnd, fcSeekToStart, fcSelectClip, fcReset, fcPlay, fcPause, fcIsEnded };
};

/* H E L P E R */

const getSeekValues = (wsTime: Fraction, { startTime, endTime }: FcRangeTime) => {
  if (wsTime < startTime) return { ws: startTime, player: 0 };
  if (wsTime >= endTime) return { ws: endTime, player: 1 };
  return { ws: wsTime, player: (wsTime - startTime) / (endTime - startTime) };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { dragger } = state.app;
  const { duration } = state.audio;
  const { fcMarkers, fcRangeTime, fcStoryConfig } = state.fc__data;
  return { duration, dragger, fcMarkers, fcRangeTime, fcStoryConfig };
};
